import { useCallback, useEffect, useRef } from "react";

import {
  RADICAL_PEER_REVIEW_MESSAGE_TYPE,
  RADICAL_PEER_REVIEW_RESULT_MESSAGE_TYPE,
} from "./creMirrorConstants";
import {
  applyPeerReviewLine,
  hasImpressionSection,
  isFullContentReplacement,
} from "../../../../utils/peerReviewLine";

/**
 * Parse the host message into a desired state.
 * Accepts `status: "requested" | "cancelled" | "canceled"` or `requested: boolean`.
 * @param {unknown} data
 * @returns {boolean | null} true = requested, false = cancelled, null = not ours / invalid
 */
export function parsePeerReviewMessage(data) {
  if (!data || typeof data !== "object") return null;
  if (data.type !== RADICAL_PEER_REVIEW_MESSAGE_TYPE) return null;
  if (typeof data.requested === "boolean") return data.requested;
  const status = String(data.status || "").trim().toLowerCase();
  if (status === "requested") return true;
  if (status === "cancelled" || status === "canceled") return false;
  return null;
}

function isFromHost(event) {
  const embedded = window.parent !== window;
  return embedded ? event.source === window.parent : event.source === window;
}

function replyToHost(payload) {
  try {
    const target = window.parent !== window ? window.parent : window;
    target.postMessage(
      { type: RADICAL_PEER_REVIEW_RESULT_MESSAGE_TYPE, ...payload },
      "*",
    );
  } catch (_) {
    // ignore
  }
}

/**
 * R4V host embed — parent posts RADICAL_PEER_REVIEW into this iframe when a peer
 * review is requested / cancelled on the study.
 *
 * "requested" stays on until "cancelled":
 * - Impression present → bold "Peer review has been requested" added as its first
 *   line (only if not already there).
 * - No Impression yet → accepted (ok, pending); the line is added whenever an
 *   Impression section appears (template load, Process AI result, user adds it).
 * - If the user deletes just the line while the Impression stays, it is NOT re-added.
 * "cancelled" removes the line and stops re-adding it.
 *
 * @param {{ current?: { editor?: object } }} editorRef - RTE ref (editorRef.current.editor)
 * @param {string} transcript - current report HTML (drives re-checks)
 */
export function useRadicalPeerReviewMessage(editorRef, transcript) {
  /** true = requested, false = cancelled, null = nothing received */
  const requestedRef = useRef(null);
  /** Cancel received before the editor existed — remove once it does. */
  const pendingRemovalRef = useRef(false);
  /** Whether the Impression existed at the last check (detects it "appearing"). */
  const hadImpressionRef = useRef(false);
  const attachedEditorRef = useRef(null);

  const getEditor = useCallback(
    () => editorRef?.current?.editor || null,
    [editorRef],
  );

  /** Add the line if requested and an Impression exists; reply when it was added. */
  const ensureLine = useCallback(() => {
    if (requestedRef.current !== true) return;
    const editor = getEditor();
    hadImpressionRef.current = hasImpressionSection(editor);
    if (!hadImpressionRef.current) return;
    const result = applyPeerReviewLine(editor, true);
    if (result.changed) {
      replyToHost({ status: "requested", ok: true, changed: true, pending: false });
    }
  }, [getEditor]);

  // Host messages.
  useEffect(() => {
    function onHostPeerReview(event) {
      const requested = parsePeerReviewMessage(event?.data);
      if (requested === null) return;
      if (!isFromHost(event)) return;

      requestedRef.current = requested;
      const editor = getEditor();
      const result = applyPeerReviewLine(editor, requested);
      hadImpressionRef.current = hasImpressionSection(editor);

      if (requested) {
        pendingRemovalRef.current = false;
        const pending = !result.ok; // no editor / no Impression yet — still on
        replyToHost({
          status: "requested",
          ok: true,
          changed: result.changed,
          pending,
        });
      } else {
        pendingRemovalRef.current = result.reason === "no_editor";
        replyToHost({
          status: "cancelled",
          ok: true,
          changed: result.changed,
          pending: pendingRemovalRef.current,
        });
      }
    }

    window.addEventListener("message", onHostPeerReview);
    return () => window.removeEventListener("message", onHostPeerReview);
  }, [getEditor]);

  // Whole-report replacements (Process AI / template load use setContent without
  // an update event): treat as a new Impression and re-ensure the line.
  // The RTE ref can exist before TipTap is fully mounted, so wait until the
  // editor event API is available before attaching the transaction listener.
  useEffect(() => {
    let cancelled = false;
    let detach = () => {};

    const bindEditor = () => {
      const editor = getEditor();
      if (!editor?.on || !editor?.off) {
        if (!cancelled) window.requestAnimationFrame(bindEditor);
        return;
      }
      if (attachedEditorRef.current === editor) return;

      attachedEditorRef.current = editor;
      const onTransaction = ({ transaction }) => {
        if (requestedRef.current !== true) return;
        if (!isFullContentReplacement(transaction)) return;
        // Defer: never dispatch from inside another transaction's handler.
        setTimeout(() => {
          hadImpressionRef.current = false;
          ensureLine();
        }, 0);
      };

      editor.on("transaction", onTransaction);
      detach = () => {
        editor.off("transaction", onTransaction);
        if (attachedEditorRef.current === editor) {
          attachedEditorRef.current = null;
        }
      };
    };

    bindEditor();
    return () => {
      cancelled = true;
      detach();
    };
  }, [getEditor, ensureLine]);

  // Report edits: add the line when the Impression appears; finish a pending cancel.
  useEffect(() => {
    const editor = getEditor();
    if (!editor) return;

    if (requestedRef.current === false && pendingRemovalRef.current) {
      const result = applyPeerReviewLine(editor, false);
      if (result.ok) {
        pendingRemovalRef.current = false;
        replyToHost({
          status: "cancelled",
          ok: true,
          changed: result.changed,
          pending: false,
        });
      }
      return;
    }

    if (requestedRef.current !== true) return;
    const hasImpression = hasImpressionSection(editor);
    if (hasImpression && !hadImpressionRef.current) {
      ensureLine();
    } else {
      hadImpressionRef.current = hasImpression;
    }
  }, [transcript, getEditor, ensureLine]);
}
